import { apiErrorText } from '@/views/Admin/AccessControl/utils';
/** @format */

import axios, { AxiosError } from 'axios';
import { ApiRes, Decision, DivisionOption, ReviewDetail, ReviewRow, ReviewStatus } from './types';

const BASE = 'accessReview/';

/** POST and return the whole response; refused calls (status false or HTTP error) throw with the server message */
const post = async <T>(path: string, payload: object = {}): Promise<ApiRes<T>> => {
  let data: ApiRes<T>;
  try {
    ({ data } = await axios.post<ApiRes<T>>(BASE + path, payload));
  } catch (error) {
    const e = error as AxiosError<{ message?: string }>;
    throw new Error(apiErrorText(e));
  }
  if (!data?.status) throw new Error(data?.message || 'The request was refused');
  return data;
};

export const reviewKeys = {
  all: ['access-review'] as const,
  pending: ['access-review', 'pending'] as const,
  list: (scope: 'MINE' | 'ALL', status: ReviewStatus | null) => ['access-review', 'list', scope, status] as const,
  detail: (id: number) => ['access-review', 'detail', id] as const,
};

/** open reviews I must act on */
export const getPending = async () => (await post<{ count: number }>('pending')).result?.count ?? 0;

export const getReviews = async (payload: { scope: 'MINE' | 'ALL'; status?: ReviewStatus | null }) => {
  const res = await post<ReviewRow[] | { rows: ReviewRow[] }>('list', {
    scope: payload.scope,
    ...(payload.status ? { status: payload.status } : {}),
  });
  const r = res.result;
  return Array.isArray(r) ? r : r?.rows ?? [];
};

export const getReview = async (id: number) => (await post<ReviewDetail>('get', { id })).result;

/** decision null clears; Remove needs a reason */
export const decideItem = (payload: { item_id: number; decision: Decision | null; reason?: string | null }) =>
  post<unknown>('decide', { ...payload, reason: payload.reason ?? null });

/** keeps every undecided line you may decide (optionally one employee) */
export const keepRest = (payload: { id: number; emp_code?: string | null }) => post<{ cnt: number }>('keepRest', payload);

/** applies removals, stores the fingerprint and locks the review */
export const signOff = (payload: { id: number; note: string | null }) => post<unknown>('signOff', payload);

export const cancelReview = (payload: { id: number; note: string }) => post<unknown>('cancel', payload);

export const startReview = (payload: { comp_code?: string | null; divn_code: string; due_dt: string; notify: boolean }) =>
  post<unknown>('start', payload);

export const startAll = (payload: { due_days: number; notify: boolean }) => post<unknown>('startAll', payload);

/** divisions of the user's company, for the start dialog */
export const getDivisions = async (): Promise<DivisionOption[]> => {
  let data: ApiRes<{ rows: DivisionOption[] }>;
  try {
    ({ data } = await axios.get<ApiRes<{ rows: DivisionOption[] }>>('orgChart/divisions'));
  } catch (error) {
    throw new Error((error as AxiosError<{ message?: string }>)?.response?.data?.message || 'Could not load divisions');
  }
  if (!data?.status) throw new Error(data?.message || 'Could not load divisions');
  return data.result?.rows ?? [];
};

/** the signed (or draft) review as a PDF, saved by the browser */
export const downloadReport = async (id: number, fileName: string) => {
  let blob: Blob;
  try {
    ({ data: blob } = await axios.post<Blob>(BASE + 'report', { id }, { responseType: 'blob' }));
  } catch (error) {
    // a refusal comes back as JSON inside the blob
    const e = error as AxiosError<unknown>;
    let msg = e?.message || 'Could not create the report';
    const errBlob = e?.response?.data;
    if (errBlob instanceof Blob) {
      try {
        msg = JSON.parse(await errBlob.text())?.message || msg;
      } catch {
        /* not JSON */
      }
    }
    throw new Error(msg);
  }
  if (blob.type && blob.type.includes('json')) {
    let msg = 'Could not create the report';
    try {
      msg = JSON.parse(await blob.text())?.message || msg;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  saveBlob(blob, fileName);
};

export const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
